import React, { useState } from "react";
import { toast } from "sonner";
import { Coins, Loader2, Clock, ChevronDown, ChevronUp, CreditCard } from "lucide-react";
import api, { apiError } from "@/lib/api";

const METHOD_LABELS = { wave: "Wave", orange_money: "Orange Money", card: "Carte bancaire", interac: "Interac" };

const fmtPrice = (price, currency) =>
  currency === "CAD" ? `${price.toLocaleString("fr-CA")} $CA` : `${price.toLocaleString("fr-FR")} FCFA`;

export const VideoCreditsPanel = ({ credits, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [pack, setPack] = useState(null);
  const [method, setMethod] = useState(null);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  if (!credits) return null;

  const methods = credits.methods || ["wave", "orange_money"];
  const activeMethod = method || methods[0];
  const pending = credits.pending || [];
  const isCard = activeMethod === "card";
  const number = credits.payment_numbers?.[activeMethod] || "";
  const methodLabel = METHOD_LABELS[activeMethod] || activeMethod;
  const packAmount = pack ? fmtPrice(pack.price, pack.currency) : "le montant du pack";

  const submitManual = async () => {
    if (!pack) { toast.error("Choisissez un pack de crédits."); return; }
    setBusy(true);
    try {
      await api.post("/marketing/credits/purchase", { credits: pack.credits, method: activeMethod, reference: reference.trim() });
      toast.success("Demande envoyée ! Vos crédits seront ajoutés dès validation de votre paiement.");
      setOpen(false); setPack(null); setReference("");
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Envoi impossible."); }
    finally { setBusy(false); }
  };

  const payByCard = async () => {
    if (!pack) { toast.error("Choisissez un pack de crédits."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/credits/checkout", { credits: pack.credits, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Paiement indisponible.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-[#F9F6F0] border border-[#E5E0D8] p-3 mb-3" data-testid="video-credits-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-[#E85D04]" />
          <span className="text-sm font-bold text-[#1A1A1A]" data-testid="video-credits-balance">
            Crédits vidéo : {credits.balance}
          </span>
        </div>
        <button
          data-testid="video-credits-recharge-btn"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1 text-xs font-bold text-[#E85D04]"
        >
          Recharger {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {pending.length > 0 && (
        <div className="mt-2 rounded-lg bg-[#FFF6EE] border border-[#F3D9C2] p-2 flex items-start gap-1.5" data-testid="video-credits-pending">
          <Clock className="h-3.5 w-3.5 text-[#E85D04] mt-0.5 shrink-0" />
          <p className="text-[11px] text-[#5C574F]">
            {pending.map((p) => `${p.credits} crédits (${fmtPrice(p.amount ?? p.amount_fcfa ?? 0, p.currency || "XOF")})`).join(" · ")} en
            attente de validation. Vos crédits seront ajoutés dès confirmation du paiement.
          </p>
        </div>
      )}

      {open && (
        <div className="mt-3" data-testid="video-credits-form">
          <p className="text-xs font-semibold text-[#5C574F] mb-1.5">Choisissez un pack</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(credits.packs || []).map((p) => (
              <button
                key={p.credits}
                data-testid={`credit-pack-${p.credits}`}
                onClick={() => setPack(p)}
                className={`rounded-xl px-2 py-2 text-center border transition-colors ${pack?.credits === p.credits ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#1A1A1A] border-[#E5E0D8]"}`}
              >
                <span className="block text-sm font-extrabold">{p.credits} cr.</span>
                <span className={`block text-[11px] ${pack?.credits === p.credits ? "text-white/70" : "text-[#9c968a]"}`}>{fmtPrice(p.price, p.currency)}</span>
              </button>
            ))}
          </div>

          <p className="text-xs font-semibold text-[#5C574F] mb-1.5">Payer par</p>
          <div className="flex gap-2 mb-2">
            {methods.map((m) => (
              <button
                key={m}
                data-testid={`credit-method-${m}`}
                onClick={() => setMethod(m)}
                className={`flex-1 h-10 rounded-xl text-sm font-semibold border transition-colors ${activeMethod === m ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#5C574F] border-[#E5E0D8]"}`}
              >
                {METHOD_LABELS[m] || m}
              </button>
            ))}
          </div>

          {isCard ? (
            <>
              <div className="rounded-lg bg-white border border-[#E5E0D8] p-2.5 mb-2" data-testid="credit-payment-instructions">
                <p className="text-xs text-[#5C574F]">
                  Paiement sécurisé par carte bancaire (Visa, Mastercard…). Vos crédits sont ajoutés
                  <strong className="text-[#1A1A1A]"> automatiquement</strong> dès le paiement confirmé.
                </p>
              </div>
              <button
                data-testid="credit-card-pay-btn"
                onClick={payByCard}
                disabled={busy}
                className="w-full h-11 rounded-xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {pack ? `Payer ${fmtPrice(pack.price, pack.currency)} par carte` : "Payer par carte"}
              </button>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-white border border-[#E5E0D8] p-2.5 mb-2" data-testid="credit-payment-instructions">
                {number ? (
                  <p className="text-xs text-[#5C574F]">
                    Envoyez <strong className="text-[#1A1A1A]">{packAmount}</strong>
                    {activeMethod === "interac"
                      ? <> par virement <strong>Interac</strong> à : <strong className="text-[#E85D04]">{number}</strong></>
                      : <> au numéro {methodLabel} : <strong className="text-[#E85D04]">{number}</strong></>}
                    , puis indiquez la référence de la transaction ci-dessous et validez.
                  </p>
                ) : (
                  <p className="text-xs text-[#5C574F]">
                    Envoyez {packAmount} via {methodLabel} (coordonnées communiquées par le support), puis indiquez la
                    référence de la transaction ci-dessous.
                  </p>
                )}
              </div>
              <input
                data-testid="credit-reference-input"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Référence de la transaction (recommandé)"
                maxLength={80}
                className="w-full h-11 bg-white rounded-xl px-3 text-sm outline-none border border-[#E5E0D8] focus:border-[#E85D04] mb-2"
              />
              <button
                data-testid="credit-submit-btn"
                onClick={submitManual}
                disabled={busy}
                className="w-full h-11 rounded-xl bg-[#E85D04] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                J'ai envoyé le paiement
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
